/**
 * Phase I — /admin/entitlements (Pass-the-paywall).
 * Admin can grant/revoke any tier without touching Stripe.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

const TIERS = [
  { id: "platform",           label: "Platform" },
  { id: "florida_transfer",   label: "Florida Transfer" },
  { id: "selective_elite",    label: "Selective & Elite" },
  { id: "transfer_audit",     label: "Transfer Audit (one-off)" },
];

function AccessChip({ source }) {
  if (source === "paid") return <span className="inline-block border border-brass bg-brass/10 text-ivy px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-semibold" data-testid="access-chip-paid">Paid</span>;
  if (source === "granted") return <span className="inline-block border border-ivy bg-ivy/10 text-ivy px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-semibold" data-testid="access-chip-granted">Granted</span>;
  if (source === "both") return (
    <span className="inline-flex text-[10px] uppercase tracking-[0.18em] font-semibold" data-testid="access-chip-both">
      <span className="border border-brass bg-brass/10 text-ivy px-2 py-0.5">Paid</span>
      <span className="border border-l-0 border-ivy bg-ivy/10 text-ivy px-2 py-0.5">+ Granted</span>
    </span>
  );
  return <span className="text-[10px] uppercase tracking-[0.18em] text-stone">None</span>;
}

function GrantModal({ open, onClose, onSaved, prefillUserId }) {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tier, setTier] = useState("platform");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/users", { params: { q, limit: 20 } }).then((r) => setUsers(r.data || []));
  }, [q, open]);

  useEffect(() => {
    if (!open || !prefillUserId || selected) return;
    api.get("/admin/users", { params: { q: "", limit: 100 } }).then((r) => {
      const match = (r.data || []).find((u) => u.user_id === prefillUserId);
      if (match) setSelected(match);
    });
  }, [open, prefillUserId, selected]);

  const submit = async () => {
    if (!selected) { toast.error("Select a user first"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/entitlements/grant", { user_id: selected.user_id, tier, reason });
      if (r.data.warning === "user_already_has_paid_tier") toast.warning("User already has this tier via paid — grant recorded for audit.");
      else toast.success(`Granted ${tier} to ${selected.email}`);
      onSaved?.();
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (typeof d === "object" && d?.error === "duplicate_grant") toast.error(d.message || "Duplicate grant");
      else toast.error(typeof d === "string" ? d : "Grant failed");
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-ink/70 z-50 flex items-start justify-center py-10 px-4 overflow-y-auto" onClick={onClose} data-testid="ent-grant-modal">
      <div className="bg-bone border border-stone/40 w-full max-w-lg p-6 lg:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">New tier grant</div>
            <h2 className="mt-2 font-serif text-2xl text-ivy tracking-tight">Grant tier access</h2>
          </div>
          <button onClick={onClose} className="text-stone hover:text-ink text-2xl leading-none">×</button>
        </div>
        <div className="space-y-5">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] text-stone mb-2">User</label>
            {selected ? (
              <div className="border border-ivy bg-ivy/5 p-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-ink">{selected.name || selected.email}</div>
                  <div className="text-xs text-stone">{selected.email} · tier: {selected.tier || "—"} · {selected.access_source || "no access"}</div>
                </div>
                <button onClick={() => setSelected(null)} className="text-[11px] uppercase tracking-[0.18em] text-brass hover:text-ivy" data-testid="ent-grant-clear-user">Change</button>
              </div>
            ) : (
              <>
                <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name…" className="w-full border border-stone/40 bg-bone px-3 py-2 text-sm outline-none focus:border-ivy" data-testid="ent-grant-user-search" />
                <ul className="mt-2 border border-stone/40 bg-bone max-h-40 overflow-y-auto">
                  {users.slice(0, 8).map((u) => (
                    <li key={u.user_id}>
                      <button onClick={() => setSelected(u)} className="w-full text-left px-3 py-2 text-sm hover:bg-bone-dim border-b border-stone/20" data-testid={`ent-grant-user-${u.user_id}`}>
                        <div className="text-ink">{u.name || u.email}</div>
                        <div className="text-xs text-stone">{u.email} · {u.access_source || "no access"}</div>
                      </button>
                    </li>
                  ))}
                  {users.length === 0 && <li className="px-3 py-2 text-xs text-stone italic">No matches.</li>}
                </ul>
              </>
            )}
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] text-stone mb-2">Tier</label>
            <div className="grid grid-cols-2 gap-0 border-t border-l border-stone/40">
              {TIERS.map((t) => (
                <button key={t.id} type="button" onClick={() => setTier(t.id)} className={`border-r border-b border-stone/40 text-left p-3 ${tier === t.id ? "bg-ivy text-bone" : "bg-bone text-ink hover:bg-bone-dim"}`} data-testid={`ent-grant-tier-${t.id}`}>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold">{t.label}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] text-stone mb-2">Reason (optional, internal audit)</label>
            <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder="e.g. 'Comp for beta feedback', 'Referral partner'…" className="w-full border border-stone/40 bg-bone px-3 py-2 text-sm outline-none focus:border-ivy resize-y" data-testid="ent-grant-reason" />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="text-[11px] uppercase tracking-[0.22em] text-stone hover:text-ivy">Cancel</button>
            <button onClick={submit} disabled={busy || !selected} className="bg-ivy hover:bg-ivy/90 text-bone uppercase tracking-[0.18em] text-xs font-semibold py-3 px-6 disabled:opacity-50" data-testid="ent-grant-submit">{busy ? "Granting…" : "Grant access"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function HistoryDrawer({ userId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!userId) return;
    api.get(`/admin/entitlements/user/${userId}`).then((r) => setData(r.data));
  }, [userId]);
  if (!userId) return null;
  return (
    <div className="fixed inset-0 bg-ink/70 z-50 flex items-start justify-end" onClick={onClose} data-testid="ent-history-drawer">
      <div className="bg-bone border-l border-stone/40 w-full max-w-xl h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Grant history</div>
            <h2 className="mt-2 font-serif text-2xl text-ivy tracking-tight">{data?.user?.name || data?.user?.email || "Loading…"}</h2>
            <div className="text-xs text-stone mt-1">{data?.user?.email} · tier: {data?.user?.tier || "—"} · <AccessChip source={data?.user?.access_source} /></div>
          </div>
          <button onClick={onClose} className="text-stone hover:text-ink text-2xl leading-none">×</button>
        </div>
        <ul className="space-y-3">
          {(data?.grants || []).map((g) => (
            <li key={g.grant_id} className={`border ${g.status === "active" ? "border-ivy bg-ivy/5" : "border-stone/40 bg-bone-dim/30"} p-3`} data-testid={`ent-history-row-${g.grant_id}`}>
              <div className="flex items-baseline justify-between">
                <div className="text-sm text-ink font-semibold">{g.tier}</div>
                <div className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${g.status === "active" ? "text-ivy" : "text-stone"}`}>{g.status}</div>
              </div>
              <div className="text-xs text-stone mt-1">granted {(g.granted_at || "").slice(0, 10)}{g.revoked_at ? ` · revoked ${(g.revoked_at || "").slice(0, 10)}` : ""}</div>
              {g.reason && <div className="text-xs text-ink-soft italic mt-1">&ldquo;{g.reason}&rdquo;</div>}
            </li>
          ))}
          {(data?.grants || []).length === 0 && <li className="text-sm text-stone italic">No grants for this user.</li>}
        </ul>
      </div>
    </div>
  );
}

export default function AdminEntitlements() {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("active");
  const [tier, setTier] = useState("");
  const [q, setQ] = useState("");
  const [grantOpen, setGrantOpen] = useState(false);
  const [historyUser, setHistoryUser] = useState(null);
  const prefillUserId = searchParams.get("prefill_user_id") || "";

  useEffect(() => {
    if (prefillUserId) setGrantOpen(true);
  }, [prefillUserId]);

  const load = () => {
    const params = { page_size: 100 };
    if (status) params.status = status;
    if (tier) params.tier = tier;
    if (q) params.q = q;
    api.get("/admin/entitlements", { params }).then((r) => setItems(r.data?.items || []));
  };
  useEffect(() => { load(); }, [status, tier, q]);

  const revoke = async (grantId, tierLabel) => {
    if (!window.confirm(`Revoke this ${tierLabel} grant?\n\nIf the user has no other grants and no active Stripe subscription, they will lose access to that tier.`)) return;
    try {
      await api.post(`/admin/entitlements/${grantId}/revoke`);
      toast.success("Grant revoked");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    }
  };

  return (
    <div data-testid="admin-entitlements-page">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl text-ivy tracking-tight">Entitlements</h1>
          <p className="text-sm text-stone mt-1">Pass-the-paywall tier grants. Silent, permanent, audit-logged. Never touches Stripe.</p>
        </div>
        <button onClick={() => setGrantOpen(true)} className="bg-ivy hover:bg-ivy/90 text-bone uppercase tracking-[0.18em] text-xs font-semibold py-3 px-5" data-testid="ent-grant-open-btn">+ Grant tier</button>
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <input type="search" placeholder="Search email or name…" value={q} onChange={(e) => setQ(e.target.value)} className="border border-stone/40 bg-bone px-3 py-2 text-sm outline-none focus:border-ivy min-w-[220px]" data-testid="ent-filter-q" />
        <div className="flex border border-stone/40">
          {[["active","Active"],["revoked","Revoked"],["","All"]].map(([v,l]) => (
            <button key={v || "all"} onClick={() => setStatus(v)} className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 ${status === v ? "bg-ivy text-bone" : "text-ink hover:bg-stone/10"}`} data-testid={`ent-filter-status-${v || "all"}`}>{l}</button>
          ))}
        </div>
        <select value={tier} onChange={(e) => setTier(e.target.value)} className="border border-stone/40 bg-bone px-3 py-2 text-sm" data-testid="ent-filter-tier">
          <option value="">All tiers</option>
          {TIERS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </div>

      <div className="border border-stone/30 bg-bone">
        <table className="w-full text-sm">
          <thead className="bg-bone-dim/40 text-[10px] uppercase tracking-[0.18em] text-brass">
            <tr>
              <th className="text-left px-4 py-2.5">User</th>
              <th className="text-left px-4 py-2.5">Tier</th>
              <th className="text-left px-4 py-2.5">Access</th>
              <th className="text-left px-4 py-2.5">Reason</th>
              <th className="text-left px-4 py-2.5">Granted</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="text-center text-stone px-4 py-10 italic">No grants.</td></tr>}
            {items.map((g) => (
              <tr key={g.grant_id} className="border-t border-stone/20 hover:bg-bone-dim/20" data-testid={`ent-row-${g.grant_id}`}>
                <td className="px-4 py-2.5">
                  <button onClick={() => setHistoryUser(g.user_id)} className="text-ivy hover:text-brass text-left" data-testid={`ent-row-user-${g.grant_id}`}>
                    <div className="font-medium">{g.user?.name || g.user?.email || g.user_id}</div>
                    <div className="text-xs text-stone">{g.user?.email}</div>
                  </button>
                </td>
                <td className="px-4 py-2.5 text-[11px] uppercase tracking-[0.18em] text-ivy font-semibold">{g.tier}</td>
                <td className="px-4 py-2.5"><AccessChip source={g.user?.access_source} /></td>
                <td className="px-4 py-2.5 text-xs text-ink-soft italic max-w-[200px] truncate" title={g.reason}>{g.reason || "—"}</td>
                <td className="px-4 py-2.5 text-xs text-stone tabular-nums">{(g.granted_at || "").slice(0, 10)}</td>
                <td className={`px-4 py-2.5 text-[10px] uppercase tracking-[0.18em] font-semibold ${g.status === "active" ? "text-ivy" : "text-stone"}`}>{g.status}</td>
                <td className="px-4 py-2.5 text-right">
                  {g.status === "active" && (
                    <button onClick={() => revoke(g.grant_id, g.tier)} className="text-[11px] uppercase tracking-[0.18em] text-crimson hover:text-crimson/80" data-testid={`ent-revoke-${g.grant_id}`}>Revoke</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <GrantModal open={grantOpen} onClose={() => setGrantOpen(false)} onSaved={load} prefillUserId={prefillUserId} />
      <HistoryDrawer userId={historyUser} onClose={() => setHistoryUser(null)} />
    </div>
  );
}
